import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { usePostHog, useIntercom } from "@finitless/shared/hooks";
import { useTranslation } from "react-i18next";
import { Icon } from "../../";

const ITSACHECKMATE_LOGO_URL =
  "https://cdn.prod.website-files.com/66130da33ae6c5ad17ca9089/6616986b5f882822b20abeed_Checkmate%20Logo%20-%20Color.svg";

const ItsaCheckmateCard = () => {
  const { t } = useTranslation("integrations");
  const { isFeatureEnabled } = usePostHog();
  const { showMessenger } = useIntercom();
  const posConfigureEnabled = isFeatureEnabled("pos_configure_enabled");

  const [isLoadingScript, setIsLoadingScript] = useState(!window.hbspt);
  const [logoError, setLogoError] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    // Load HubSpot forms script if not already loaded
    if (!window.hbspt) {
      const script = document.createElement("script");
      script.src = "//js.hsforms.net/forms/embed/v2.js";
      script.charset = "utf-8";
      script.type = "text/javascript";
      script.async = true;

      script.onload = () => {
        if (isMountedRef.current) {
          setIsLoadingScript(false);
        }
      };

      script.onerror = () => {
        if (isMountedRef.current) {
          setIsLoadingScript(false);
        }
        console.error("Failed to load HubSpot forms script");
      };

      document.body.appendChild(script);
    } else {
      setIsLoadingScript(false);
    }

    // Cleanup function
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleContactUs = (e) => {
    e.stopPropagation();

    // Prevent multiple modals
    if (document.getElementById("hubspot-modal")) {
      return;
    }

    // Track timeouts and retry count for cleanup
    let formLoadTimeout = null;
    let retryTimeout = null;
    let retryCount = 0;
    const MAX_RETRIES = 50; // Max 5 seconds of retries (50 * 100ms)

    // Create modal container
    const modalContainer = document.createElement("div");
    modalContainer.id = "hubspot-modal";
    modalContainer.style.cssText = `
      position: fixed;
      inset: 0;
      background-color: rgba(0, 0, 0, 0.75);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
      padding: 1rem;
    `;

    // Create modal content
    const modalContent = document.createElement("div");
    modalContent.style.cssText = `
      background-color: #1f2937;
      border-radius: 0.5rem;
      padding: 2rem;
      max-width: 600px;
      width: 100%;
      min-height: 500px;
      max-height: 90vh;
      overflow-y: auto;
      position: relative;
    `;

    // Create close button with proper event handlers
    const closeButton = document.createElement("button");
    closeButton.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>
    `;
    closeButton.style.cssText = `
      position: absolute;
      top: 1rem;
      right: 1rem;
      background: transparent;
      border: none;
      color: #9ca3af;
      cursor: pointer;
      padding: 0.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 0.25rem;
      transition: all 0.2s;
    `;

    // Use addEventListener instead of inline handlers for proper cleanup
    const handleMouseOver = () => {
      closeButton.style.color = "#ffffff";
      closeButton.style.backgroundColor = "#374151";
    };
    const handleMouseOut = () => {
      closeButton.style.color = "#9ca3af";
      closeButton.style.backgroundColor = "transparent";
    };

    closeButton.addEventListener("mouseover", handleMouseOver);
    closeButton.addEventListener("mouseout", handleMouseOut);

    // Cleanup function to clear all timeouts and event listeners
    const cleanup = () => {
      if (formLoadTimeout) clearTimeout(formLoadTimeout);
      if (retryTimeout) clearTimeout(retryTimeout);

      // Remove event listeners
      closeButton.removeEventListener("mouseover", handleMouseOver);
      closeButton.removeEventListener("mouseout", handleMouseOut);
      closeButton.removeEventListener("click", cleanup);
      modalContainer.removeEventListener("click", handleBackdropClick);

      // Remove modal from DOM
      if (document.body.contains(modalContainer)) {
        document.body.removeChild(modalContainer);
      }
    };

    closeButton.addEventListener("click", cleanup);

    // Create form container
    const formContainer = document.createElement("div");
    formContainer.id = "hubspot-form-container";
    formContainer.style.cssText = `
      margin-top: 1rem;
    `;

    // Create loading skeleton
    const skeleton = document.createElement("div");
    skeleton.id = "hubspot-loading-skeleton";
    skeleton.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: 1.25rem;
      animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    `;
    skeleton.innerHTML = `
      <style>
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      </style>
      <!-- First name & Last name row -->
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 1rem;">
        <div>
          <div style="height: 1rem; width: 5rem; background-color: #4b5563; border-radius: 0.25rem; margin-bottom: 0.5rem;"></div>
          <div style="height: 2.75rem; background-color: #374151; border-radius: 0.375rem;"></div>
        </div>
        <div>
          <div style="height: 1rem; width: 5rem; background-color: #4b5563; border-radius: 0.25rem; margin-bottom: 0.5rem;"></div>
          <div style="height: 2.75rem; background-color: #374151; border-radius: 0.375rem;"></div>
        </div>
      </div>
      <!-- Email field -->
      <div>
        <div style="height: 1rem; width: 3rem; background-color: #4b5563; border-radius: 0.25rem; margin-bottom: 0.5rem;"></div>
        <div style="height: 2.75rem; background-color: #374151; border-radius: 0.375rem;"></div>
      </div>
      <!-- Phone number field -->
      <div>
        <div style="height: 1rem; width: 7rem; background-color: #4b5563; border-radius: 0.25rem; margin-bottom: 0.5rem;"></div>
        <div style="display: grid; grid-template-columns: 150px 1fr; gap: 0.75rem;">
          <div style="height: 2.75rem; background-color: #374151; border-radius: 0.375rem;"></div>
          <div style="height: 2.75rem; background-color: #374151; border-radius: 0.375rem;"></div>
        </div>
      </div>
      <!-- Meeting Type dropdown -->
      <div>
        <div style="height: 1rem; width: 11rem; background-color: #4b5563; border-radius: 0.25rem; margin-bottom: 0.5rem;"></div>
        <div style="height: 2.75rem; background-color: #374151; border-radius: 0.375rem;"></div>
      </div>
      <!-- Submit button -->
      <div style="height: 2.75rem; background-color: #6b7280; border-radius: 0.375rem; width: 50%; margin-top: 0.5rem;"></div>
    `;
    formContainer.appendChild(skeleton);

    // Create title
    const title = document.createElement("h2");
    title.textContent = "Contact Us - ItsaCheckmate";
    title.style.cssText = `
      color: #ffffff;
      font-size: 1.5rem;
      font-weight: 600;
      margin-bottom: 1rem;
      padding-right: 2rem;
    `;

    modalContent.appendChild(closeButton);
    modalContent.appendChild(title);
    modalContent.appendChild(formContainer);
    modalContainer.appendChild(modalContent);
    document.body.appendChild(modalContainer);

    // Close on backdrop click with proper event listener
    const handleBackdropClick = (e) => {
      if (e.target === modalContainer) {
        cleanup();
      }
    };
    modalContainer.addEventListener("click", handleBackdropClick);

    // Load HubSpot form with minimum display time for skeleton
    const loadForm = () => {
      if (window.hbspt) {
        // Add minimum delay to show skeleton (800ms)
        formLoadTimeout = setTimeout(() => {
          // Check if modal still exists before manipulating DOM
          if (!document.body.contains(modalContainer)) return;

          // Remove skeleton
          const skeletonElement = document.getElementById(
            "hubspot-loading-skeleton",
          );
          if (skeletonElement) {
            skeletonElement.remove();
          }

          window.hbspt.forms.create({
            portalId: "46622027",
            formId: "75de65b2-e874-4be8-8f33-d126fb5cf387",
            region: "na1",
            target: "#hubspot-form-container",
            onFormSubmitted: () => {
              // Show success toast and close modal
              toast.success("Form submitted successfully");
              cleanup();
            },
          });
        }, 800);
      } else {
        // If HubSpot script not loaded yet, wait and try again
        retryCount++;
        if (retryCount < MAX_RETRIES) {
          retryTimeout = setTimeout(loadForm, 100);
        } else {
          // Max retries reached, show error
          console.error("HubSpot script failed to load after maximum retries");
          toast.error("Failed to load contact form. Please try again later.");
          cleanup();
        }
      }
    };

    loadForm();
  };

  return (
    <div className="flex h-full flex-col rounded-lg border border-gray-800 bg-gray-900 p-6 transition-colors hover:bg-gray-800/50">
      {/* Header */}
      <div className="mb-4">
        <div className="mb-3 flex items-start justify-between">
          <div className="flex items-start space-x-3">
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-700 bg-white">
              {!logoError ? (
                <img
                  src={ITSACHECKMATE_LOGO_URL}
                  alt="ItsaCheckmate"
                  className="h-full w-full object-contain p-1"
                  onError={() => setLogoError(true)}
                />
              ) : (
                <span className="text-lg font-bold text-white">IC</span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="mb-1 text-lg font-semibold text-white">
                ItsaCheckmate
              </h3>
              <p className="text-sm text-gray-400">Checkmate integration</p>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-auto space-y-2 border-t border-gray-800 pt-4">
        {posConfigureEnabled ? (
          <button
            onClick={handleContactUs}
            disabled={isLoadingScript}
            className="flex w-full items-center justify-center rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:from-blue-700 hover:to-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isLoadingScript ? (
              <>
                <svg
                  className="mr-2 -ml-1 h-4 w-4 animate-spin text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span>Loading...</span>
              </>
            ) : (
              <span>Contact us</span>
            )}
          </button>
        ) : (
          <button
            onClick={(e) => {
              e.stopPropagation();
              showMessenger();
            }}
            className="flex w-full items-center justify-center rounded-lg bg-gray-700 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-600"
          >
            <Icon name="MessageCircle" className="mr-2 h-4 w-4" />
            <span>{t("contactUs")}</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default ItsaCheckmateCard;
